import ImageProps, { LayerProps, ProjectSaveSnapshot } from "../../types/imageStudio/images";
import { unzipImageStudioProjectFile, zipImageStudioProjectFile } from "../fileHandling";
import { composeImageWithLayers, createImageThumbnailFile, fileToDataUrl } from "../images/imageConvertFormats";
import { ApiDataStorage } from "../../apis/ApiDataStorage";
import { canvasToFile, fileToImageProps } from "../images/imagesFunctions";
import Cookies from "js-cookie";

//returns basename for project zip, png and thumbnail if running without errors
export const uploadProjectData = async(base: ImageProps, layer:LayerProps[], folderPath:string,createdAt:Date,thumbnailSize:number): Promise<{baseName: string, thumbnail:File} | null> => {
    const baseName = getProjectBaseNameFromImage(base);
    const thumbnailName = createProjectThumbnailName(baseName);
    const fullPngName = `${baseName}.png`;
    const [projectZip, thumbnail, fullCanvas] = await Promise.all([
        zipImageStudioProjectFile(base, layer, createdAt, baseName),
        createImageThumbnailFile(base, layer, thumbnailName, thumbnailSize),
        composeImageWithLayers(base, layer)
    ]);

    const fullPng = await canvasToFile(fullCanvas, fullPngName);

    if(!projectZip || !thumbnail) return null;

    try {
        const thumbnailForm = new FormData();
        thumbnailForm.append("file", thumbnail, thumbnail.name);

        const fullPngForm = new FormData();
        fullPngForm.append("file", fullPng, fullPng.name);

        const [projectOk, thumbnailOk, pngOk] = await Promise.all([
            ApiDataStorage.PostImagePropject(folderPath, projectZip.path, projectZip.zip),
            ApiDataStorage.PostImagePropject(folderPath, thumbnailName, thumbnailForm),
            ApiDataStorage.PostImagePropject(folderPath, fullPngName, fullPngForm)
        ]);

        if (!projectOk || !thumbnailOk || !pngOk) return null;

        return {
            baseName,
            thumbnail
        };
    } catch (error) {
        console.error("uploadProjectData failed:", error);
        return null;
    } 
};

export const deleteProjectData = async(folderPath:string,baseName:string): Promise<boolean> => {
    //deletes thumbnail and project data if exists
    const projectZipName = `${folderPath}/${baseName}.sngImg`;
    const thumbnailName = `${folderPath}/${baseName}.thumbnail`;
    const fullPngName = `${folderPath}/${baseName}.png`;

    try{
        await ApiDataStorage.DeleteImageProject(projectZipName);
        await ApiDataStorage.DeleteImageProject(thumbnailName);
        await ApiDataStorage.DeleteImageProject(fullPngName);
    }
    catch{
        return false;
    }
    return true;    
};

export const getAllThumbnails = async (folderPath: string): Promise<File[]> => {
    try {
        const entries = await ApiDataStorage.ListImageProjectEntries(folderPath);

        const thumbnailEntries = entries
            .filter(item => item.key.endsWith(".thumbnail"))
            .sort((a, b) => {
                const aTime = new Date(a.last_modified).getTime();
                const bTime = new Date(b.last_modified).getTime();
                return bTime - aTime;
            });

        const thumbnails: File[] = [];

        for (const entry of thumbnailEntries) {
            const filename = entry.key.replace(folderPath, "");
            const item = await ApiDataStorage.GetImageProject(folderPath, filename);

            if (item) {
                thumbnails.push(item);
            }
        }

        return thumbnails;
    } catch {
        return [];
    }
};
export const getProjectPngNameFromThumbnail = (thumbnailName: string) => {
    return `${stripKnownImageStudioExtensions(thumbnailName)}.png`;
};

export const loadProjectPngFromThumbnail = async (
    thumbnailFile: File,
    folderPath: string
): Promise<File> => {
    const pngName = getProjectPngNameFromThumbnail(thumbnailFile.name);

    try {
        const pngFile = await ApiDataStorage.GetImageProject(folderPath, pngName);
        return pngFile ?? thumbnailFile;
    } catch {
        return thumbnailFile;
    }
};
export const readHistoryFiles = async(history:string[],MAIN_DATA_PATH_IMAGES:string) => {
    const historyFiles:File[] = [];
    for(let imgPath of history){
        let imgFile = await ApiDataStorage.GetImageProject(MAIN_DATA_PATH_IMAGES,imgPath);
        if(imgFile){
            historyFiles.push(imgFile);
        }
    }
    return historyFiles;
};

export const updateCookieHistory = (History_Cookie_name: string,thumbnailFile: File) => {
  const currentHistory = Cookies.get(History_Cookie_name);
  const convertedHistory = currentHistory
    ? JSON.parse(currentHistory) as string[]
    : [];
  const newHistory = [
    thumbnailFile.name,
    ...convertedHistory.filter(name => name !== thumbnailFile.name)
  ];
  Cookies.set(History_Cookie_name, JSON.stringify(newHistory));
};

export const openImageProject = async(thumbnailName:string,folderPath:string=''): Promise<ProjectSaveSnapshot | null> => {
    const projectName = thumbnailName.replace('.thumbnail','.sngImg');
    const projectData = await ApiDataStorage.GetImageProject(folderPath,projectName);
    if(!projectData) return null;

    const unzippedProject = await unzipImageStudioProjectFile(projectData);
    if(!unzippedProject.baseFile) return null;

    const baseImg = await fileToImageProps(unzippedProject.baseFile);
    const layer: LayerProps[] = [];
    for(let layerData of unzippedProject.description.layer){
        let singleLayer: LayerProps = {...layerData};
        if(singleLayer.type === 'drawings'){
            //add matching image to layer
            const layerImg = unzippedProject.layerFiles.find(file => file.name.replace(/\.[^.]+$/, "") === singleLayer.layerID.toString());
            if(layerImg && singleLayer.drawingData){
                const layerImgSrc = await fileToDataUrl(layerImg)
                singleLayer.drawingData.rasterSrc = layerImgSrc;
            }
        }
        else if (singleLayer.type === "ai-variation" || singleLayer.type === "image") {
            const layerImg = unzippedProject.layerFiles.find(file => file.name.replace(/\.[^.]+$/, "") === singleLayer.layerID.toString());

            if (layerImg) {
                const imageProps = await fileToImageProps(layerImg);

                singleLayer.imageData = {
                    ...imageProps,
                    title: singleLayer.imageData?.title ?? imageProps.title,
                    alt: singleLayer.imageData?.alt ?? imageProps.alt
                };

                singleLayer.imageEdit = {
                    ...imageProps,
                    title: singleLayer.imageEdit?.title ?? imageProps.title,
                    alt: singleLayer.imageEdit?.alt ?? imageProps.alt
                };
            }
        }
        layer.push(singleLayer);
    }
    console.log('Opened Layers: ', layer)
    return {
        image: baseImg,
        createdAt: unzippedProject.description.createdAt,
        layers: layer
    }
};

export const stripKnownImageStudioExtensions = (name: string) => {
    return name
        .replace(/\.thumbnail$/i, "")
        .replace(/\.sngImg$/i, "")
        .replace(/\.(png|jpg|jpeg|webp|gif|tif|tiff|svg|pdf)$/i, "");
};

export const getProjectBaseNameFromImage = (image?: ImageProps | null) => {
    const raw =
        image?.file?.name ||
        image?.title ||
        `UnnamedFile-${crypto.randomUUID()}`;

    return stripKnownImageStudioExtensions(raw);
};

export const createProjectBaseImageFileName = (baseName: string) => {
    return `${stripKnownImageStudioExtensions(baseName)}.png`;
};

export const createProjectThumbnailName = (baseName: string) => {
    return `${stripKnownImageStudioExtensions(baseName)}.thumbnail`;
};

export const createProjectZipName = (baseName: string) => {
    return `${stripKnownImageStudioExtensions(baseName)}.sngImg`;
};